import { TypedDocumentNode, gql, useMutation } from '@apollo/client';
import { useState } from 'react';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { MissionRow } from '@sorare/core/src/components/missions/MissionRow';
import { BulletPoint } from '@sorare/core/src/components/missions/MissionRow/BulletPoint';
import { MissionStatus } from '@sorare/core/src/components/missions/MissionRow/MissionStatus';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { Color } from '@sorare/core/src/style/types';

import { RewardPreview } from 'components/missions/Mission/RewardPreview';
import { RewardBoxDialogWithOnboarding } from 'components/rewards/RewardBoxDialogWithOnboarding';

import type {
  ClaimMissionMutation,
  ClaimMissionMutationVariables,
  MissionProbabilisticBundleQuery,
  MissionProbabilisticBundleQueryVariables,
  Mission_managerProgressionMission,
} from './__generated__/index.graphql';

type Props = {
  mission: Mission_managerProgressionMission;
  bgColor: Color;
  color: Color;
  onClaim?: () => void;
};

const MISSION_PROBABILISTIC_BUNDLE_QUERY = gql`
  query MissionProbabilisticBundleQuery($id: ID!) {
    probabilisticBundle(id: $id) {
      id
      ...RewardBoxDialogWithOnboarding_probabilisticBundle
    }
  }
  ${RewardBoxDialogWithOnboarding.fragments.probabilisticBundle}
` as TypedDocumentNode<
  MissionProbabilisticBundleQuery,
  MissionProbabilisticBundleQueryVariables
>;

export const CLAIM_MISSION_TASK_MUTATION = gql`
  mutation ClaimMissionMutation($name: ManagerProgressionTaskSlug!) {
    claimManagerProgressionTask(input: { name: $name, sport: FOOTBALL }) {
      managerProgressionTaskOrMission {
        id
        aasmState
      }
      errors {
        message
        code
      }
    }
  }
  ${RewardBoxDialogWithOnboarding.fragments.probabilisticBundle}
` as TypedDocumentNode<ClaimMissionMutation, ClaimMissionMutationVariables>;

export const Mission = ({ mission, bgColor, color, onClaim }: Props) => {
  const [claimManagerProgressionMission, { loading }] = useMutation(
    CLAIM_MISSION_TASK_MUTATION
  );
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);

  const claimable = mission.aasmState === ManagerProgressionTaskState.COMPLETED;
  const probabilisticBundleId = mission.probabilisticBundles.at(0)?.id;
  const reward = mission.configRewards.at(0);

  const { data } = useQuery(MISSION_PROBABILISTIC_BUNDLE_QUERY, {
    variables: { id: probabilisticBundleId || '' },
    skip: !probabilisticBundleId || !claimable,
  });
  const probabilisticBundle = data?.probabilisticBundle;

  return (
    <>
      <MissionRow
        checked={[
          ManagerProgressionTaskState.COMPLETED,
          ManagerProgressionTaskState.CLAIMED,
        ].includes(mission.aasmState)}
        title={<LabelL bold>{mission.title}</LabelL>}
        description={<LabelM>{mission.description}</LabelM>}
        claimable={claimable}
        status={
          <MissionStatus
            show={[
              ManagerProgressionTaskState.COMPLETED,
              ManagerProgressionTaskState.CLAIMED,
              ManagerProgressionTaskState.CANCELED,
            ].includes(mission.aasmState)}
            claimable={claimable}
            onClaim={
              onClaim ||
              (async () => {
                await claimManagerProgressionMission({
                  variables: { name: mission.name },
                });
                setRewardDialogOpen(true);
              })
            }
            loading={loading}
            status={mission.aasmState}
          />
        }
        rewards={reward ? <RewardPreview reward={reward} /> : null}
        bgColor={bgColor}
        color={color}
        bulletPoint={
          <BulletPoint
            checked={[
              ManagerProgressionTaskState.COMPLETED,
              ManagerProgressionTaskState.CLAIMED,
            ].includes(mission.aasmState)}
            color={color}
          />
        }
      />
      {!!rewardDialogOpen && probabilisticBundle && (
        <RewardBoxDialogWithOnboarding
          open
          onClose={() => {
            setRewardDialogOpen(false);
          }}
          bundle={probabilisticBundle}
        />
      )}
    </>
  );
};
Mission.fragments = {
  managerProgressionMission: gql`
    fragment Mission_managerProgressionMission on ManagerProgressionMission {
      id
      name
      aasmState
      title
      description
      configRewards {
        ...RewardPreview_managerTaskRewardInterface
      }
      probabilisticBundles {
        id
      }
    }
    ${RewardPreview.fragments.managerTaskRewardInterface}
  ` as TypedDocumentNode<Mission_managerProgressionMission>,
};
